import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useSkillSyncInProgress } from "../../hooks/useSkillSyncInProgress";
import { connectorNameWithAuth } from "../../lib/connector";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import {
  Field,
  FieldLabel,
  Fields,
  FieldValue,
  LinkField,
  Row,
  ValueField,
} from "../GitSourceFields";
import { SettingsCard as Card, SettingsHeading } from "../SettingsCard";
import { Text } from "../Text";

const Header = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const ResyncButton = styled(Button)`
  &&& {
    background: var(--alpha4);
  }
  &&&:hover:not(:disabled) {
    background: var(--alpha8);
  }
  &&&.syncing {
    opacity: 1;
  }
  &.syncing > span:first-child {
    animation: ${spin} 2s linear infinite;
  }
`;

type Props = {
  skillId: string;
  canWrite: boolean;
  installation: {
    sourcePath: string;
    lastSyncedCommit: string | null;
    syncStartedAt: number | null;
    lastSyncedAt: number | null;
    lastError: string | null;
    source?: {
      remoteUrl: string;
      ref: string | null;
      connectedAppId: string | null;
      resolvedCommit: string | null;
      lastError: string | null;
      connectedApp?: { name: string; identifier: string } | undefined;
    };
  };
};

export function GitSourceCard({ skillId, canWrite, installation }: Props) {
  const z = useZero();
  const [confirming, setConfirming] = useState(false);
  const source = installation.source;
  const canUseConnector = useHasAccess(
    "connected_app:use",
    source?.connectedAppId ?? undefined
  );
  const syncing = useSkillSyncInProgress(installation.syncStartedAt);
  if (!source) return null;

  const stale = installation.syncStartedAt !== null && !syncing;
  const canResync =
    canWrite && (source.connectedAppId === null || canUseConnector);
  const commit = installation.lastSyncedCommit ?? source.resolvedCommit;
  const error = installation.lastError ?? source.lastError;
  const connectorLabel = source.connectedApp
    ? connectorNameWithAuth(source.connectedApp)
    : null;

  const repoWebUrl = source.remoteUrl.replace(/\.git$/, "");

  return (
    <>
      <Card>
        <Header>
          <SettingsHeading>
            <Text variant="text-regular-md">Git source</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Installed files can be refreshed from their repository.
            </Text>
          </SettingsHeading>
          <ResyncButton
            className={syncing ? "syncing" : undefined}
            variant="secondary"
            icon="refresh-cw"
            disabled={!canResync || syncing}
            onClick={() => setConfirming(true)}
          >
            Resync
          </ResyncButton>
        </Header>
        <Fields>
          <LinkField
            label="Repository"
            value={source.remoteUrl}
            href={repoWebUrl}
          />
          <Row>
            <ValueField
              label="Branch or tag"
              value={source.ref ?? "Default branch"}
              title={source.ref ?? undefined}
            />
            <ValueField
              label="Path"
              value={installation.sourcePath}
              title={installation.sourcePath}
            />
            <ValueField
              label="Commit"
              value={commit ?? "Not synced yet"}
              title={commit ?? undefined}
            />
            {connectorLabel && (
              <ValueField
                label="Connector"
                value={connectorLabel}
                title={connectorLabel}
              />
            )}
            <Field>
              <FieldLabel>
                <Text variant="text-small-md" color="var(--text-primary)">
                  Status
                </Text>
              </FieldLabel>
              <FieldValue>
                <span style={{ color: error ? "var(--red-600)" : undefined }}>
                  {syncing
                    ? "Syncing…"
                    : error
                      ? error
                      : stale
                        ? "Sync timed out — try again"
                        : installation.lastSyncedAt
                          ? `Synced ${formatRelativeTime(installation.lastSyncedAt)}`
                          : "Not synced yet"}
                </span>
              </FieldValue>
            </Field>
          </Row>
        </Fields>
      </Card>
      <ConfirmModal
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          z.mutate(
            sharedMutators.resyncSkill({
              skillId,
              connectedAppId: source.connectedAppId,
            })
          );
          setConfirming(false);
        }}
        title="Resync skill?"
        description="Replaces every skill file with the current files from the Git repository. Local edits will be overwritten."
        confirmLabel="Resync"
      />
    </>
  );
}
